import React, { useState, useEffect } from 'react';
import { SocialPost } from '../../../core/types/social';
import { Heart, MessageCircle, MapPin, Bookmark, Share2 } from 'lucide-react';
import { ShareToSocialModal } from './ShareToSocialModal';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { interactionService } from '../../../core/services/firebase/interactionService';
import { useAuth } from '../../auth/AuthContext';
import { useSocial } from '../SocialContext';
import { CommentsModal } from './CommentsModal';
import { VerificationBadge } from './VerificationBadge';
import { FormattedContent } from './FormattedContent';
import { storageHelper } from '../../../core/utils/storageHelper'; // Added

interface PostCardProps {
  post: SocialPost;
  authorName?: string;
  authorAvatar?: string;
  authorVerification?: {
    level: 'none' | 'silver' | 'gold' | 'blue';
    isShadow: boolean;
  };
  onUpdate?: () => void;
}

export const PostCard: React.FC<PostCardProps> = ({ 
  post, 
  authorName, 
  authorAvatar, 
  authorVerification,
  onUpdate 
}) => {
  const { user } = useAuth();
  const { profile } = useSocial();
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likesCount || 0);
  const [commentsCount, setCommentsCount] = useState(post.commentsCount || 0);
  const [showComments, setShowComments] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const timeAgo = formatDistanceToNow(new Date(post.createdAt), { 
    addSuffix: true,
    locale: ptBR 
  });

  // Check if user has liked this post
  useEffect(() => {
    if (user && post.id) {
      interactionService.hasUserLiked(post.id, user.uid)
        .then(setIsLiked)
        .catch(console.error);
    }
  }, [user, post.id]);

  // Check if user has saved this post
  useEffect(() => {
    if (user && post.id) {
      interactionService.hasUserSaved(post.id, user.uid)
        .then(setIsSaved)
        .catch(console.error);
    }
  }, [user, post.id]);

  const handleLike = async () => {
    if (!user || !profile || isLiking) return;

    setIsLiking(true);
    try {
      if (isLiked) {
        await interactionService.unlikePost(post.id, user.uid);
        setIsLiked(false);
        setLikesCount(prev => Math.max(0, prev - 1));
      } else {
        await interactionService.likePost(
          post.id, 
          user.uid, 
          profile.handle,
          profile.displayName,
          profile.avatarUrl
        );
        setIsLiked(true);
        setLikesCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const handleSave = async () => {
    if (!user || !profile || isSaving) return;

    setIsSaving(true);
    try {
      if (isSaved) {
        await interactionService.unsavePost(post.id, user.uid);
        setIsSaved(false);
      } else {
        await interactionService.savePost(post.id, user.uid, profile.handle);
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Error toggling save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCommentAdded = () => {
    setCommentsCount(prev => prev + 1);
    if (onUpdate) onUpdate();
  };

  return (
    <>
      <article className="bg-brand-charcoal/30 rounded-3xl border border-white/10 overflow-hidden backdrop-blur-3xl group/post">
        {/* Header */}
        <div className="p-4 flex items-center gap-3">
          <a 
            href={`/${post.authorId}`}
            className="w-12 h-12 rounded-2xl bg-white/5 flex-shrink-0 overflow-hidden hover:ring-2 hover:ring-brand-gold/30 transition-all border border-white/10"
          >
            {authorAvatar ? (
              <img 
                src={storageHelper.getAvatarThumbnail(authorAvatar)} 
                alt={authorName} 
                className="w-full h-full object-cover" 
                loading="lazy" 
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/20 font-black">
                {(authorName || post.authorId)[0].toUpperCase()}
              </div>
            )}
          </a>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <a 
                  href={`/${post.authorId}`}
                  className="font-black text-white hover:text-brand-gold transition-colors block truncate uppercase tracking-tight"
                >
                  {authorName || post.authorId}
                </a>
                <VerificationBadge 
                  level={authorVerification?.level} 
                  isShadow={authorVerification?.isShadow} 
                  size={14}
                />
              </div>
              {post.category && (
                <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-[0.15em] border ${
                  post.category === 'catch' ? 'bg-brand-orange/10 text-brand-orange border-brand-orange/20' :
                  post.category === 'technique' ? 'bg-brand-gold/10 text-brand-gold border-brand-gold/20' :
                  post.category === 'location' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                  post.category === 'equipment' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' :
                  'bg-white/5 text-white/40 border-white/10'
                }`}>
                  {post.category === 'catch' ? 'Captura' : 
                   post.category === 'technique' ? 'Técnica' : 
                   post.category === 'location' ? 'Local' : 
                   post.category === 'equipment' ? 'Equip' : 'Relato'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-white/20">
              <span>{timeAgo}</span>
              {post.checkIn && (
                <>
                  <span className="opacity-50">•</span>
                  <span className="flex items-center gap-1 text-brand-gold cursor-pointer hover:underline transition-all">
                    <MapPin size={10} fill="currentColor" />
                    {post.checkIn.businessName}
                  </span>
                </>
              )}
              {post.location && !post.checkIn && (
                <>
                  <span className="opacity-50">•</span>
                  <span className="flex items-center gap-1 hover:text-brand-gold cursor-pointer transition-colors">
                    <MapPin size={10} className="text-brand-orange" />
                    {post.location}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        {(post.content || (post.tags && post.tags.length > 0)) && (
          <div className="px-4 pb-3">
            {post.content && (
              <FormattedContent 
                content={post.content} 
                className="text-white/80 text-[15px] leading-relaxed font-medium" 
              />
            )}
            
            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {post.tags.map((tag, idx) => (
                  <button 
                    key={idx}
                    className="text-brand-gold font-black text-xs uppercase tracking-widest hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Filter logic will be added to FeedPage
                    }}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Media Gallery */}
        {post.mediaUrls && post.mediaUrls.length > 0 && (
          <div className={`
            grid gap-0.5
            ${post.mediaUrls.length === 1 ? 'grid-cols-1' : ''}
            ${post.mediaUrls.length === 2 ? 'grid-cols-2' : ''}
            ${post.mediaUrls.length >= 3 ? 'grid-cols-2' : ''}
          `}>
            {post.mediaUrls.slice(0, 4).map((url, index) => (
              <div 
                key={index}
                className={`
                  relative bg-gray-100 overflow-hidden
                  ${post.mediaUrls.length === 1 ? 'aspect-[4/3]' : 'aspect-square'}
                  ${index === 3 && post.mediaUrls.length > 4 ? 'relative' : ''}
                `}
              >
                <img 
                  src={storageHelper.getPostPreview(url)} 
                  alt={`Post media ${index + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                />
                {index === 3 && post.mediaUrls.length > 4 && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-2xl font-bold">
                    +{post.mediaUrls.length - 4}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="px-6 py-4 flex items-center gap-8 border-t border-white/5 bg-white/[0.02]">
          <button 
            onClick={handleLike}
            disabled={!user || isLiking}
            className={`
              flex items-center gap-2.5 transition-all group/btn
              ${isLiked ? 'text-brand-orange' : 'text-white/30 hover:text-white'}
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            <Heart 
              size={18} 
              className={`group-hover/btn:scale-110 transition-transform ${isLiked ? 'fill-current' : ''}`}
            />
            <span className="text-xs font-black uppercase tracking-widest">{likesCount}</span>
          </button>

          <button 
            onClick={() => setShowComments(true)}
            className="flex items-center gap-2.5 text-white/30 hover:text-white transition-all group/btn"
          >
            <MessageCircle size={18} className="group-hover/btn:scale-110 transition-transform" />
            <span className="text-xs font-black uppercase tracking-widest">{commentsCount}</span>
          </button>

          {/* Botão Compartilhar nas Redes */}
          {post.mediaUrls && post.mediaUrls.length > 0 && (
            <button
              onClick={() => setShowShareModal(true)}
              className="flex items-center gap-2 text-white/30 hover:text-amber-400 transition-all group/btn"
              title="Compartilhar nas redes sociais"
            >
              <Share2 size={18} className="group-hover/btn:scale-110 transition-transform" />
            </button>
          )}

          <button 
            onClick={handleSave}
            disabled={!user || isSaving}
            className={`
              ml-auto flex items-center gap-2 transition-all group/btn
              ${isSaved ? 'text-brand-gold' : 'text-white/30 hover:text-white'}
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
            title={isSaved ? "Remover dos favoritos" : "Salvar nos favoritos"}
          >
            <Bookmark 
              size={18} 
              className={`group-hover/btn:scale-110 transition-transform ${isSaved ? 'fill-current' : ''}`}
            />
          </button>
        </div>
      </article>

      {/* Comments Modal */}
      {showComments && (
        <CommentsModal
          post={post}
          authorName={authorName}
          authorAvatar={authorAvatar}
          onClose={() => setShowComments(false)}
          onCommentAdded={handleCommentAdded}
        />
      )}

      {/* Share Modal */}
      {showShareModal && post.mediaUrls && post.mediaUrls.length > 0 && (
        <ShareToSocialModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          postId={post.id}
          userId={user?.uid || ''}
          imageUrl={post.mediaUrls[0]}
          initialCaption={post.content}
          location={post.location}
          checkIn={post.checkIn ? {
            tenantId: post.checkIn.tenantId,
            name: post.checkIn.businessName,
          } : undefined}
        />
      )}
    </>
  );
};
